/**
 * UserWithdrawTable Component
 * 탈퇴 요청 목록 테이블
 */
import React from 'react';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import { WithdrawRequest } from '../types/userWithdraw.types';

const ITEMS_PER_PAGE = 15;

// ======== 타입 ========

interface UserWithdrawTableProps {
    withdrawRequests: WithdrawRequest[];
    currentItems: WithdrawRequest[];
    currentPage: number;
    totalPages: number;
    selectedItems: Set<string>;
    isAllSelected: boolean;
    onSelectAll: () => void;
    onSelectItem: (riderId: string, suppId: string) => void;
    onApprove: () => void;
    onPageChange: (page: number) => void;
}

// ======== 헬퍼 ========

const isApprovedItem = (item: WithdrawRequest): boolean =>
    item.withdrawalStatusCd === 'APPROVED' || item.withdrawalStatusCdName === '승인';

// ======== 컴포넌트 ========

const UserWithdrawTable: React.FC<UserWithdrawTableProps> = ({
                                                                 withdrawRequests,
                                                                 currentItems,
                                                                 currentPage,
                                                                 totalPages,
                                                                 selectedItems,
                                                                 isAllSelected,
                                                                 onSelectAll,
                                                                 onSelectItem,
                                                                 onApprove,
                                                                 onPageChange,
                                                             }) => {
    const hasSelectableItems = withdrawRequests.some(item => !isApprovedItem(item));

    return (
        <>
            {/* 툴바 */}
            <div className="user-withdraw-toolbar">
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                        className="select-all-btn"
                        onClick={onSelectAll}
                        disabled={withdrawRequests.length === 0}
                    >
                        {isAllSelected ? '전체 해제' : '전체 선택'}
                    </button>
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                        선택: {selectedItems.size}개
                    </span>
                </div>
                <button
                    className="btn btn-primary withdraw-btn"
                    onClick={onApprove}
                    disabled={selectedItems.size === 0}
                >
                    <i className="fas fa-check" />
                    탈퇴
                </button>
            </div>

            {/* 테이블 컨테이너 */}
            <div className="user-withdraw-table-container">
                <div className="user-withdraw-table-wrapper">
                    <table className="user-withdraw-table">
                        <thead>
                        <tr>
                            <th style={{ width: '50px' }}>
                                {hasSelectableItems && (
                                    <input
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={onSelectAll}
                                        disabled={withdrawRequests.length === 0}
                                    />
                                )}
                            </th>
                            <th style={{ width: '80px' }}>NO</th>
                            <th>라이더 ID</th>
                            <th>라이더명</th>
                            <th>협력사 ID</th>
                            <th>협력사명</th>
                            <th>계약 상태</th>
                            <th>탈퇴 요청 상태</th>
                            <th>탈퇴 요청 일시</th>
                            <th style={{ textAlign: 'right' }}>잔액</th>
                            <th style={{ textAlign: 'right' }}>미납 금액</th>
                        </tr>
                        </thead>
                        <tbody>
                        {withdrawRequests.length === 0 ? (
                            <tr>
                                <td colSpan={11} style={{ textAlign: 'center', padding: '2rem' }}>
                                    조회된 탈퇴 요청이 없습니다.
                                </td>
                            </tr>
                        ) : (
                            currentItems.map((request, index) => {
                                const tableIndex = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
                                const itemKey = `${request.riderId}-${request.suppId}`;
                                const isSelected = selectedItems.has(itemKey);
                                const isApproved = isApprovedItem(request);
                                return (
                                    <tr key={itemKey}>
                                        <td>
                                            {!isApproved && (
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => onSelectItem(request.riderId, request.suppId)}
                                                />
                                            )}
                                        </td>
                                        <td>{tableIndex}</td>
                                        <td>{request.riderId}</td>
                                        <td>{request.riderName || '-'}</td>
                                        <td>{request.suppId}</td>
                                        <td>{request.suppName || '-'}</td>
                                        <td>{request.contStatusName || request.contStatus}</td>
                                        <td>
                                                <span className={`badge ${request.withdrawalStatusCd === 'REQUEST' ? 'badge-warning' : 'badge-secondary'}`}>
                                                    {request.withdrawalStatusCdName || '-'}
                                                </span>
                                        </td>
                                        <td>{request.withdrawalReqDateTime || '-'}</td>
                                        <td style={{ textAlign: 'right' }}>{request.balAmtFormatted}</td>
                                        <td style={{ textAlign: 'right' }}>{request.unpaidAmtFormatted}</td>
                                    </tr>
                                );
                            })
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 페이지네이션 */}
            {withdrawRequests.length > 0 && (
                <div className="pagination-container" style={{ display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={onPageChange}
                    />
                </div>
            )}
        </>
    );
};

export default UserWithdrawTable;
